// Exploration leads in the Capital — faint rings and stepping-stone dots on the ground that point toward real, undiscovered
// landmarks. Subtle on purpose (low opacity, additive, only noticed when you're near), warm cream for the first steps and cool
// jade for the leads that only open once you've found something else. See docs/CAPITAL-LEADS.md and game/capitalLeadRules.js.
import * as THREE from 'three'
import { LeadConfig as L } from '../config/capital.js'
import { isLeadOpen, ringAlpha, dotAlpha, newlyOpened } from '../game/capitalLeadRules.js'
import { Events, emit } from '../ui/eventBus.js'

const WARM = 0xffe6a8
const JADE = 0x9fe8c6

const additive = (color) => new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0, depthWrite: false, blending: THREE.AdditiveBlending, side: THREE.DoubleSide, toneMapped: false })

export async function loadTrails(base) {
  const r = await fetch(`${base}/capital_trails.json`)
  if (!r.ok) throw new Error(`trails ${r.status}`)
  return r.json()
}

// root = the Capital's scene root (children are in source metres). pois = the landmark list. isFound(id) / isAdmitted() read real state.
export function createCapitalLeads({ root, trails, pois, isFound, isAdmitted }) {
  const name = Object.fromEntries(pois.map((p) => [p.id, p.name]))
  const ringGeo = new THREE.RingGeometry(0.86, 1, 64); ringGeo.rotateX(-Math.PI / 2)
  const discGeo = new THREE.CircleGeometry(0.86, 40); discGeo.rotateX(-Math.PI / 2)
  const dotGeo = new THREE.RingGeometry(0.6, 1, 28); dotGeo.rotateX(-Math.PI / 2)
  const LIFT = 0.07 // metres above the floor, so the markers never z-fight with it

  const entries = trails.trails.map((t) => {
    const color = t.from === 'market' ? WARM : JADE // first steps are warm; leads that had to be earned are cool
    const group = new THREE.Group()
    group.visible = false
    const ringMat = additive(color), discMat = additive(color)
    const ring = new THREE.Mesh(ringGeo, ringMat)
    const disc = new THREE.Mesh(discGeo, discMat)
    ring.position.set(t.dest[0], t.dest[2] + LIFT, t.dest[1])
    disc.position.copy(ring.position)
    ring.renderOrder = disc.renderOrder = 5
    group.add(disc, ring)
    const dots = t.dots.map(([x, z, h], i) => {
      const m = new THREE.Mesh(dotGeo, additive(color))
      m.position.set(x, h + LIFT, z)
      m.scale.setScalar(L.dotRadiusM)
      m.renderOrder = 5
      m.userData.phase = i * 0.9
      group.add(m)
      return m
    })
    root.add(group)
    return { t, group, ring, disc, ringMat, discMat, dots, open: false }
  })

  const wasOpen = new Set()
  let time = 0
  let first = true

  function tick(dt, hx, hz) {
    time += dt
    const admitted = isAdmitted()
    // Say so (once) when new leads open — except at the first look of a visit, when whatever is already open is just there.
    const opened = newlyOpened(trails.trails, wasOpen, isFound, admitted)
    if (!first && opened.length) {
      const names = [...new Set(opened.map((l) => name[l.to] ?? l.to))]
      emit(Events.CapitalLeadsOpened, { names, gated: opened.some((l) => l.admitted), from: [...new Set(opened.map((l) => name[l.from] ?? l.from))] })
    }
    first = false
    const pulse = 0.82 + 0.18 * Math.sin(time * 2.1)
    for (const e of entries) {
      const open = isLeadOpen(e.t, isFound, admitted)
      e.open = open
      e.group.visible = open
      if (!open) continue
      const d = Math.hypot(hx - e.t.dest[0], hz - e.t.dest[1])
      const a = ringAlpha(d) * pulse
      e.ringMat.opacity = a
      e.discMat.opacity = a * 0.22
      e.ring.visible = e.disc.visible = a > 0.004
      const r = L.ringRadiusM * (1 + 0.05 * Math.sin(time * 2.1))
      e.ring.scale.setScalar(r); e.disc.scale.setScalar(r)
      for (const m of e.dots) {
        const da = dotAlpha(Math.hypot(hx - m.position.x, hz - m.position.z)) * (0.8 + 0.2 * Math.sin(time * 2.1 + m.userData.phase))
        m.material.opacity = da
        m.visible = da > 0.004
      }
    }
  }

  // For tests / dev tools: which leads are showing and how bright their rings are.
  const state = () => entries.map((e) => ({ from: e.t.from, to: e.t.to, open: e.open, gated: !!e.t.admitted, ring: +e.ringMat.opacity.toFixed(3), dots: e.dots.filter((m) => m.visible).length }))
  return { tick, state }
}
